// Per-river detail drawer (right-side overlay).
//
// Replaces the full-page route pattern. The overview (map + card grid) stays
// mounted underneath; the drawer slides in from the right when a river is
// selected. URL still tracks selection (#/r/:id) for deep-linking, but the
// map keeps its state across selections — no remount, no flicker.
//
// For rivers in BOTH categories, both narratives are shown stacked so the
// user gets the full context regardless of which mode they're in.

import type { RiverAnalysis, RiverCategory } from '../../functions/_lib/types';
// charts.ts is imported lazily inside mountHydrograph() — P017.
import { fetchRiverDetail } from '../lib/api';
import {
  formatCfs,
  formatPercent,
  formatTimeAgo,
  percentileBadgeClass,
  shortPercentileLabel,
  speciesShortList,
  statusBadgeClass,
  statusFor,
  statusLabel,
  trendIcon,
} from '../lib/format';
import { getMode, setMode } from '../lib/mode';

type CloseHandler = () => void;

let drawerEl: HTMLElement | null = null;
let backdropEl: HTMLElement | null = null;
let currentId: string | null = null;
let currentRiver: RiverAnalysis | null = null;
let escHandler: ((e: KeyboardEvent) => void) | null = null;
let onClose: CloseHandler | null = null;

export function isDrawerOpen(): boolean {
  return drawerEl !== null;
}

export function currentDrawerId(): string | null {
  return currentId;
}

// Element that triggered the drawer open. Focus returns here on close.
let openingElement: HTMLElement | null = null;

export async function openDrawer(
  id: string,
  fallback: RiverAnalysis | undefined,
  opts: { onClose: CloseHandler },
): Promise<void> {
  onClose = opts.onClose;

  if (drawerEl && currentId === id) {
    // Already showing this river — refresh in place.
    if (fallback) renderInto(drawerEl, fallback);
    void hydrate(id);
    return;
  }

  // Capture the trigger element BEFORE the drawer takes focus.
  openingElement = (document.activeElement as HTMLElement | null) ?? null;

  if (!drawerEl) mountChrome();
  currentId = id;
  currentRiver = fallback ?? null;

  if (drawerEl) {
    drawerEl.innerHTML = fallback ? renderBody(fallback) : renderSkeleton();
    if (fallback) mountHydrograph(fallback);
    // Trigger slide-in animation on next frame.
    requestAnimationFrame(() => {
      drawerEl?.classList.add('is-open');
      // Move focus into the dialog so keyboard / SR users land inside.
      drawerEl?.querySelector<HTMLButtonElement>('[data-close-drawer]')?.focus();
    });
    backdropEl?.classList.add('is-open');
  }

  void hydrate(id);
}

export function closeDrawer(): void {
  if (!drawerEl) return;
  drawerEl.classList.remove('is-open');
  backdropEl?.classList.remove('is-open');
  const el = drawerEl;
  const bd = backdropEl;
  drawerEl = null;
  backdropEl = null;
  currentId = null;
  currentRiver = null;
  if (escHandler) {
    window.removeEventListener('keydown', escHandler);
    escHandler = null;
  }
  // Restore focus to the element that opened the drawer.
  if (openingElement && document.contains(openingElement)) {
    openingElement.focus();
  }
  openingElement = null;
  // Wait for slide-out before removing from DOM.
  setTimeout(() => {
    el?.remove();
    bd?.remove();
  }, 280);
  onClose?.();
  onClose = null;
}

function mountChrome(): void {
  backdropEl = document.createElement('div');
  backdropEl.className = 'drawer-backdrop';
  backdropEl.addEventListener('click', () => closeDrawer());

  drawerEl = document.createElement('aside');
  drawerEl.className = 'river-drawer';
  drawerEl.setAttribute('role', 'dialog');
  drawerEl.setAttribute('aria-modal', 'true');

  document.body.appendChild(backdropEl);
  document.body.appendChild(drawerEl);

  escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeDrawer();
      return;
    }
    // Focus trap: wrap Tab navigation inside the drawer so keyboard
    // users don't accidentally escape the dialog.
    if (e.key !== 'Tab' || !drawerEl) return;
    const focusables = drawerEl.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };
  window.addEventListener('keydown', escHandler);
}

async function hydrate(id: string): Promise<void> {
  try {
    const fresh = await fetchRiverDetail(id);
    if (currentId !== id || !drawerEl) return; // user navigated away
    currentRiver = fresh;
    renderInto(drawerEl, fresh);
    mountHydrograph(fresh);
  } catch (e) {
    if (!currentRiver && drawerEl) {
      drawerEl.innerHTML = `<div class="p-6"><div class="alert alert-error">${(e as Error).message}</div></div>`;
    }
  }
}

function renderInto(host: HTMLElement, r: RiverAnalysis): void {
  host.innerHTML = renderBody(r);
  // Re-bind interactive elements.
  host.querySelector<HTMLButtonElement>('[data-close-drawer]')?.addEventListener('click', () => closeDrawer());
  host.querySelector<HTMLButtonElement>('[data-toggle-mode="whitewater"]')?.addEventListener('click', () => {
    setMode('whitewater');
    if (currentRiver) renderInto(host, currentRiver);
    if (currentRiver) mountHydrograph(currentRiver);
  });
  host.querySelector<HTMLButtonElement>('[data-toggle-mode="fly_fishing"]')?.addEventListener('click', () => {
    setMode('fly_fishing');
    if (currentRiver) renderInto(host, currentRiver);
    if (currentRiver) mountHydrograph(currentRiver);
  });
}

function renderSkeleton(): string {
  return `
    <div class="drawer-header">
      <div>
        <div class="skeleton h-3 w-24 mb-2 bg-base-300/40"></div>
        <div class="skeleton h-7 w-48 bg-base-300/40"></div>
      </div>
      <button data-close-drawer class="btn btn-sm btn-ghost btn-circle" aria-label="Close">✕</button>
    </div>
    <div class="drawer-body space-y-4">
      <div class="grid grid-cols-2 gap-3">
        ${'<div class="skeleton h-16 bg-base-300/40 rounded-lg"></div>'.repeat(4)}
      </div>
      <div class="skeleton h-48 bg-base-300/40 rounded-lg"></div>
      <div class="space-y-2">
        <div class="skeleton h-3 w-full bg-base-300/40"></div>
        <div class="skeleton h-3 w-5/6 bg-base-300/40"></div>
        <div class="skeleton h-3 w-2/3 bg-base-300/40"></div>
      </div>
    </div>
  `;
}

function renderBody(r: RiverAnalysis): string {
  const isInBoth = r.categories.includes('whitewater') && r.categories.includes('fly_fishing');
  // Pick the drawer's "primary lens" based on what the river ACTUALLY
  // supports — never force a lens the river doesn't have. For dual-cat
  // rivers, honor the user's current mode preference; for single-cat
  // rivers, lock to that lens regardless of mode.
  const userPref = getMode();
  const cat: RiverCategory = isInBoth
    ? userPref
    : (r.categories.includes('fly_fishing') ? 'fly_fishing' : 'whitewater');
  const status = statusFor(r, cat);
  const eyebrow = cat === 'fly_fishing' ? 'Fly Fishing' : 'Whitewater';

  const fcInfo = r.forecast.method === 'none'
    ? 'No forecast available'
    : r.forecast.method === 'nwps'
      ? `NOAA NWPS · issued ${formatTimeAgo(r.forecast.issued_at ?? null)}`
      : 'Trend projection (no NWPS forecast point)';

  return `
    <div class="drawer-header">
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2 mb-1.5">
          <span class="tagline-ribbon !text-[10px]">${eyebrow}</span>
          ${isInBoth
            ? `<div class="join">
                <button data-toggle-mode="whitewater" class="join-item btn btn-xs ${cat === 'whitewater' ? 'btn-primary' : 'btn-ghost'}" title="Whitewater view">🚣</button>
                <button data-toggle-mode="fly_fishing" class="join-item btn btn-xs ${cat === 'fly_fishing' ? 'btn-secondary' : 'btn-ghost'}" title="Fly fishing view">🎣</button>
              </div>`
            : ''}
        </div>
        <h2 class="font-display text-xl md:text-2xl text-primary tracking-wide truncate">${escapeHtml(r.river)}</h2>
        <p class="text-sm text-base-content/60 truncate">${escapeHtml(r.section)} · ${r.state}</p>
      </div>
      <button data-close-drawer class="btn btn-sm btn-ghost btn-circle flex-shrink-0" aria-label="Close drawer">
        <span class="text-lg">✕</span>
      </button>
    </div>

    <div class="drawer-body">
      <!-- Big current-flow stat -->
      <div class="drawer-flow-block">
        <div class="flex items-baseline justify-between gap-4 mb-2">
          <div>
            <div class="font-display text-4xl md:text-5xl text-primary leading-none">${formatCfs(r.current.cfs)}</div>
            <div class="text-[11px] uppercase tracking-widest text-base-content/50 mt-1">CFS</div>
          </div>
          <div class="text-right space-y-1">
            <div class="text-xs text-base-content/55">Updated ${formatTimeAgo(r.current.observed_at)}</div>
            <div class="flex items-center gap-1.5 justify-end">
              <span class="text-base">${trendIcon(r.trend_24h.direction)}</span>
              <span class="text-sm capitalize">${r.trend_24h.direction}</span>
              ${r.trend_24h.cfs_per_hour !== null
                ? `<span class="text-xs text-base-content/50">${Math.round(r.trend_24h.cfs_per_hour)}/hr</span>`
                : ''}
            </div>
          </div>
        </div>
        <!-- Two-stat layout: "% of normal" (the historical context number)
             + lens-appropriate status pill. We dropped the standalone
             "percentile" pill because:
              - it duplicated the "% of normal" number (30% of normal IS
                very low; the pill said the same thing twice)
              - users couldn't tell what the pill was telling them. -->
        <div class="grid grid-cols-2 gap-2 text-xs">
          <Stat label="vs normal" value="${formatPercent(r.percent_of_median)}" />
          <Stat label="${cat === 'fly_fishing' ? 'fishing' : 'runnable'}" value='<span class="badge ${statusBadgeClass(status, cat)} badge-sm whitespace-nowrap">${statusLabel(status, cat)}</span>' />
        </div>
      </div>

      <!-- Hydrograph -->
      <section class="drawer-section">
        <h3 class="drawer-section-title">30-Day Hydrograph + Forecast</h3>
        <div id="drawer-hydrograph" class="chart-host"></div>
        <div class="text-[11px] text-base-content/50 mt-2">${fcInfo}</div>
      </section>

      ${renderNarrativeBlocks(r, isInBoth, cat)}

      ${renderThresholdsBlock(r, cat)}

      ${cat === 'fly_fishing' && r.fish_species && r.fish_species.length > 0
        ? `<section class="drawer-section">
            <h3 class="drawer-section-title">Target Species</h3>
            <p class="text-sm text-base-content/80">${escapeHtml(speciesShortList(r.fish_species))}</p>
          </section>`
        : ''}

      ${r.errors.length > 0
        ? `<details class="text-xs text-base-content/45">
            <summary class="cursor-pointer">Diagnostics (${r.errors.length})</summary>
            <pre class="mt-2 text-base-content/35 whitespace-pre-wrap">${escapeHtml(r.errors.join('\n'))}</pre>
           </details>`
        : ''}
    </div>
  `.replace(/<Stat label="([^"]+)" value=('[^']+'|"[^"]+")\s*\/>/g, (_, label, val) => `
    <div class="bg-base-100/40 rounded-md px-2 py-2 text-center">
      <div class="text-[9px] uppercase tracking-wider text-base-content/45 mb-1">${label}</div>
      <div class="text-sm">${val.slice(1, -1)}</div>
    </div>
  `);
}

function renderNarrativeBlocks(r: RiverAnalysis, isInBoth: boolean, currentCat: RiverCategory): string {
  // For dual-category rivers, show BOTH narratives. Current mode first.
  const blocks: string[] = [];

  if (isInBoth) {
    const order: RiverCategory[] = currentCat === 'whitewater'
      ? ['whitewater', 'fly_fishing']
      : ['fly_fishing', 'whitewater'];
    for (const c of order) {
      blocks.push(narrativeCard(r, c));
    }
  } else {
    const c = r.categories[0] ?? currentCat;
    blocks.push(narrativeCard(r, c));
  }

  return blocks.join('');
}

function narrativeCard(r: RiverAnalysis, c: RiverCategory): string {
  const text = c === 'fly_fishing' ? r.narratives.fly_fishing : r.narratives.whitewater;
  const title = c === 'fly_fishing' ? 'Fishing Report' : 'Conditions Brief';
  const icon = c === 'fly_fishing' ? '🎣' : '🚣';
  const accent = c === 'fly_fishing' ? 'border-l-secondary' : 'border-l-primary';
  const sources = c === 'fly_fishing' ? r.sources?.fly_fishing : r.sources?.whitewater;
  const modelLabel = c === 'fly_fishing' ? 'Claude Haiku · Tavily' : 'Claude Haiku · AW + curated';

  return `
    <section class="drawer-narrative ${accent}">
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center gap-2">
          <span class="text-base">${icon}</span>
          <h3 class="font-display text-[11px] tracking-widest text-primary uppercase">${title}</h3>
        </div>
        <span class="text-[9px] text-base-content/40">via ${modelLabel}</span>
      </div>
      ${text
        ? `<p class="text-sm text-base-content/85 leading-relaxed">${escapeHtml(text)}</p>`
        : `<p class="narrative-pending text-sm leading-relaxed">${c === 'fly_fishing' ? 'Generating fishing report…' : 'Generating conditions brief…'}</p>`}
      ${renderCitations(sources)}
    </section>
  `;
}

function renderCitations(
  sources: Array<{ url: string; title: string; provider: string; published_at?: string | null }> | undefined,
): string {
  if (!sources || sources.length === 0) return '';
  // Show up to 5 chips — more than that and the list crowds the drawer.
  // Dedupe by hostname so multiple silverbow URLs collapse into one chip.
  const seen = new Set<string>();
  const deduped: typeof sources = [];
  for (const s of sources) {
    const host = safeHost(s.url);
    if (seen.has(host)) continue;
    seen.add(host);
    deduped.push(s);
    if (deduped.length >= 5) break;
  }
  const chips = deduped.map((s) => {
    const host = safeHost(s.url) || s.title.slice(0, 30);
    const date = formatSourceDate(s.published_at);
    const dateLabel = date ? `<span class="citation-date">· ${escapeHtml(date)}</span>` : '';
    return `<a href="${escapeAttr(s.url)}" target="_blank" rel="noreferrer noopener"
              class="citation-chip"
              title="${escapeAttr(s.title)} (${s.provider}${date ? ` · ${date}` : ''})">
              <span class="citation-host">${escapeHtml(host)}</span>${dateLabel}</a>`;
  }).join('');
  return `<div class="citation-row">
    <span class="citation-label">Sources</span>
    ${chips}
  </div>`;
}

/**
 * Compact source date for the chip. Prefer short forms because the chip is
 * narrow. "May 18" if same year, "May '24" if older, omit if invalid.
 */
function formatSourceDate(raw: string | null | undefined): string {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameYear = d.getUTCFullYear() === now.getUTCFullYear();
  const month = d.toLocaleString('en-US', { month: 'short' });
  const day = d.getUTCDate();
  return sameYear ? `${month} ${day}` : `${month} '${String(d.getUTCFullYear()).slice(-2)}`;
}

function safeHost(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; }
}

function escapeAttr(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

function renderThresholdsBlock(r: RiverAnalysis, cat: RiverCategory): string {
  if (cat === 'fly_fishing' && r.fishing_flow_cfs) {
    const t = r.fishing_flow_cfs;
    return `
      <section class="drawer-section">
        <h3 class="drawer-section-title">Fishing Flow Range</h3>
        <div class="grid grid-cols-4 gap-1.5 text-xs">
          ${chip('Too low', formatCfs(t.too_low))}
          ${chip('Ideal lo', formatCfs(t.ideal_low))}
          ${chip('Ideal hi', formatCfs(t.ideal_high))}
          ${chip('Too high', formatCfs(t.too_high))}
        </div>
      </section>
    `;
  }
  const t = r.flow_thresholds_cfs;
  return `
    <section class="drawer-section">
      <h3 class="drawer-section-title">Runnable Range</h3>
      <div class="grid grid-cols-5 gap-1.5 text-xs">
        ${chip('Too low', formatCfs(t.too_low))}
        ${chip('Opt lo', formatCfs(t.optimal_low))}
        ${chip('Opt hi', formatCfs(t.optimal_high))}
        ${chip('High', formatCfs(t.high))}
        ${chip('Flood', formatCfs(t.flood))}
      </div>
    </section>
  `;
}

function chip(label: string, val: string): string {
  return `
    <div class="bg-base-300/40 rounded px-2 py-1.5 text-center">
      <div class="text-base-content/40 text-[9px] uppercase tracking-wide">${label}</div>
      <div class="text-base-content/85 font-medium tabular-nums">${val}</div>
    </div>
  `;
}

function mountHydrograph(r: RiverAnalysis): void {
  queueMicrotask(async () => {
    const host = document.getElementById('drawer-hydrograph');
    if (!host) return;
    // Lazy-load ECharts (P017) — same chunk as the sparkline; vite
    // dedupes. Second call here resolves the cached module instantly.
    const { makeHydrograph } = await import('../lib/charts');
    const chart = makeHydrograph(host, {
      observed: r.hydrograph_30d,
      forecast: r.forecast,
      breakpoints: r.percentile_band?.breakpoints ?? null,
      thresholds: r.flow_thresholds_cfs,
    });
    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(host);
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
